// Public API for the framework-mapping registry.
//
// Plug-in shape: each framework lives in a single file
//   `./frameworks/<id>.framework.ts` exporting a `RegisteredFramework`
//   ({ definition, mapping }). Register it in `_registry.ts`. Done.
//
// Every lookup helper accepts an optional `frameworks` argument so callers can
// pass a resolved (admin-overridden) registry. It defaults to the static
// FRAMEWORKS, so existing callers keep working unchanged.
//
// Lookup helpers:
//   listFrameworks()                    -> definitions (includes stubs)
//   listActiveFrameworks()              -> definitions with > 0 entries
//   getFramework(id)                    -> { definition, mapping } | undefined
//   getMappingsForThreat(threatId)      -> per-framework entries + rationale
//   getThreatsForEntry(fwId, entryId)   -> threat ids touching this entry
//   getRationale(threatId, entryId)     -> rationale string | undefined
//
// Presentation helpers (re-exported below):
//   capEntries(items, max) / DISPLAY_CAPS  -> shared entry-list truncation
//   shortenFrameworkTitle(title)           -> column/chip label

export { capEntries, DISPLAY_CAPS } from "./cap";
export type { Capped } from "./cap";
export { shortenFrameworkTitle } from "./format";

import { FRAMEWORKS } from "./frameworks/_registry";
import type {
  FrameworkDefinition,
  FrameworkId,
  MappedEntry,
  RegisteredFramework,
  ThreatMappingLine,
} from "./types";

export { FRAMEWORKS };
export { mappingsContent, type MappingsContent } from "./registry-config";
export type {
  FrameworkDefinition,
  FrameworkEntry,
  FrameworkId,
  MappedEntry,
  RegisteredFramework,
  ThreatMapping,
  ThreatMappingLine,
} from "./types";
export { THREATS, THREAT_CATEGORY_LABELS } from "./threats";
export { THREAT_CATEGORIES, categoryLabelsFrom, isOperationalCategory } from "./threat-categories";
export type { ThreatId } from "./threats";

type Registry = readonly RegisteredFramework[];

/** All frameworks the registry knows about (includes empty stubs). */
export function listFrameworks(
  frameworks: Registry = FRAMEWORKS,
): readonly FrameworkDefinition[] {
  return frameworks.map((f) => f.definition);
}

/** Frameworks that actually have entries (use this for UI tabs/badges). */
export function listActiveFrameworks(
  frameworks: Registry = FRAMEWORKS,
): readonly FrameworkDefinition[] {
  return frameworks
    .filter((f) => f.definition.entries.length > 0)
    .map((f) => f.definition);
}

/** Look up one framework by ID. */
export function getFramework(
  id: FrameworkId,
  frameworks: Registry = FRAMEWORKS,
): RegisteredFramework | undefined {
  return frameworks.find((f) => f.definition.id === id);
}

/** Every framework entry an internal threat maps to (with rationale). */
export function getMappingsForThreat(
  threatId: string,
  frameworks: Registry = FRAMEWORKS,
): MappedEntry[] {
  const out: MappedEntry[] = [];
  for (const fw of frameworks) {
    for (const line of fw.mapping.lines) {
      if (line.threatId !== threatId) continue;
      for (const eid of line.entryIds) {
        const entry = fw.definition.entries.find((e) => e.id === eid);
        if (entry) {
          out.push({
            framework: fw.definition,
            entry,
            rationale: line.rationale,
          });
        }
      }
    }
  }
  return out;
}

/** Internal threat IDs that reference a given framework entry. */
export function getThreatsForEntry(
  frameworkId: FrameworkId,
  entryId: string,
  frameworks: Registry = FRAMEWORKS,
): string[] {
  const fw = getFramework(frameworkId, frameworks);
  if (!fw) return [];
  const ids = new Set<string>();
  for (const line of fw.mapping.lines) {
    if (line.entryIds.includes(entryId)) ids.add(line.threatId);
  }
  return Array.from(ids);
}

/** Rationale for a specific threat → entry pairing (if recorded). */
export function getRationale(
  threatId: string,
  entryId: string,
  frameworks: Registry = FRAMEWORKS,
): string | undefined {
  for (const fw of frameworks) {
    for (const line of fw.mapping.lines) {
      if (line.threatId === threatId && line.entryIds.includes(entryId)) {
        return line.rationale;
      }
    }
  }
  return undefined;
}

/** Mapping lines grouped per framework — used by the coverage matrix. */
export function getMappingLinesForThreat(
  threatId: string,
  frameworks: Registry = FRAMEWORKS,
): Array<{ framework: FrameworkDefinition; line: ThreatMappingLine | null }> {
  return frameworks
    .filter((f) => f.definition.entries.length > 0)
    .map((fw) => ({
      framework: fw.definition,
      line: fw.mapping.lines.find((l) => l.threatId === threatId) ?? null,
    }));
}
